import React from "react";
import { useNavigate } from "react-router-dom";
import { classMgmtService } from "services/admin/classMgmtService";
import { getCurrentBackendRole } from "utils/auth";
import type { ClassItem, ClassListItem, ClassStatus } from "types/domain";
import { MdAdd, MdEdit, MdGroups, MdVisibility } from "react-icons/md";
import Card from "components/card";
import StatusBadge from "components/ui/StatusBadge";
import FilterChip from "components/ui/FilterChip";
import EmptyState from "components/ui/EmptyState";
import { useLanguage } from "context/LanguageContext";
import CreateClassModal from "./CreateClassModal";
import "./classes-mgmt.css";

const STATUS_LABEL_KEY: Record<ClassStatus, string> = {
  OPENING: "enum.classStatus.OPENING",
  ONGOING: "enum.classStatus.ONGOING",
  CLOSED: "enum.classStatus.CLOSED",
};

const STATUS_TONE: Record<ClassStatus, "success" | "info" | "neutral"> = {
  OPENING: "success",
  ONGOING: "info",
  CLOSED: "neutral",
};

const STATUS_FILTERS: Array<{ value: ClassStatus | "ALL"; label: string }> = [
  { value: "ALL", label: "classList.filterAll" },
  { value: "OPENING", label: STATUS_LABEL_KEY.OPENING },
  { value: "ONGOING", label: STATUS_LABEL_KEY.ONGOING },
  { value: "CLOSED", label: STATUS_LABEL_KEY.CLOSED },
];

const getInitials = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "QL";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const ClassList = ({ centerId }: { centerId?: string } = {}) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const backendRole = getCurrentBackendRole();
  const canManageClass = backendRole !== "TEACHER";
  const canEnrollStudents = backendRole === "TEACHER" || canManageClass;
  const [items, setItems] = React.useState<ClassListItem[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<ClassStatus | "ALL">(
    "ALL"
  );

  const fetchAll = React.useCallback(async () => {
    setLoading(true);
    try {
      const classRes = await classMgmtService.getAll({
        limit: 100,
        center_id: centerId,
      });
      setItems(classRes.items);
      setError(null);
    } catch (e: any) {
      setError(e.message ?? t("classList.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [centerId]);

  React.useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleCreated = (created: ClassItem) => {
    setCreateModalOpen(false);
    navigate(created.id);
  };

  const filteredItems = React.useMemo(
    () =>
      statusFilter === "ALL"
        ? items
        : items.filter((item) => item.status === statusFilter),
    [items, statusFilter]
  );

  return (
    <div className="mt-5">
      <div className="mb-5 flex items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-ink">
          {t("classList.title", { count: filteredItems.length })}
        </h2>
        {canManageClass ? (
          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center gap-1.5 rounded-2xl bg-brand-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-600 active:bg-brand-700"
          >
            <MdAdd className="h-5 w-5" />
            {t("classList.addClass")}
          </button>
        ) : null}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/10 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((f) => (
          <FilterChip
            key={f.value}
            active={statusFilter === f.value}
            onClick={() => setStatusFilter(f.value)}
          >
            {t(f.label)}
          </FilterChip>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-sm text-ink-subtle">
          {t("state.loading")}
        </div>
      ) : filteredItems.length === 0 ? (
        <EmptyState
          title={
            items.length === 0 ? t("classList.empty") : t("classList.noMatch")
          }
          description={
            items.length === 0 ? undefined : t("classList.noMatchHint")
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
          {filteredItems.map((item) => (
            <Card key={item.id} extra="flex flex-col p-5">
              <div className="mb-2 flex items-start justify-between gap-2">
                <button
                  onClick={() => navigate(item.id)}
                  className="text-left text-base font-bold text-ink hover:text-brand-500 hover:underline"
                >
                  {item.name}
                </button>
                <StatusBadge
                  label={t(STATUS_LABEL_KEY[item.status])}
                  tone={STATUS_TONE[item.status]}
                  className="shrink-0"
                />
              </div>

              <div
                title={item.description ?? "—"}
                className="ui-clamp-3 mb-4 text-sm text-ink-muted"
              >
                {item.description ?? "—"}
              </div>

              <div className="mb-4 flex items-center justify-between gap-2 text-xs text-ink-muted">
                <span className="inline-flex items-center gap-1.5">
                  <MdGroups className="h-4 w-4" />
                  {t("classList.studentCount", {
                    count: item.student_count ?? 0,
                  })}
                </span>
              </div>

              <div className="mb-4">
                {item.managers.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {item.managers.map((manager) => (
                      <div
                        key={manager.id}
                        className="inline-flex max-w-[220px] items-center gap-2 rounded-xl border border-brand-200 bg-brand-50 px-2.5 py-1.5 text-xs font-medium text-brand-700 dark:border-brand-500/30 dark:bg-brand-900/20 dark:text-brand-200"
                      >
                        <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-[10px] font-bold text-brand-700 dark:bg-brand-400/30 dark:text-brand-100">
                          {getInitials(manager.full_name)}
                        </span>
                        <span className="truncate">{manager.full_name}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-ink-subtle">
                    {t("classList.noManager")}
                  </span>
                )}
              </div>

              <div className="mt-auto flex items-center gap-2 border-t border-line pt-4">
                <button
                  onClick={() => navigate(item.id)}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-900/20 dark:text-brand-200 dark:hover:bg-brand-900/35"
                >
                  <MdVisibility className="h-3.5 w-3.5" />
                  {t("action.viewDetail")}
                </button>
                {canEnrollStudents ? (
                  <button
                    onClick={() => navigate(`${item.id}/edit`)}
                    title={
                      canManageClass
                        ? t("action.edit")
                        : t("classList.manageStudents")
                    }
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-line text-ink-muted hover:bg-surface-hover"
                  >
                    <MdEdit className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}

      {canManageClass ? (
        <CreateClassModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={handleCreated}
          centerId={centerId}
        />
      ) : null}
    </div>
  );
};

export default ClassList;
